<script lang="ts" setup>
import { useSchoolStore } from "@/store/school";
import { useSessionStore } from "@/store/session";
import { TAG_TYPES, type TTag } from "@/assets/js/globalTypes.js";
import SchoolProgramsHeader from "@/components/school/school-setup/school-programs/SchoolProgramHeader.vue";
import MaterialSymbolsBookmarkOutlineRounded from "~icons/material-symbols/bookmark-outline-rounded";
import SchoolProgramsContent from "@/components/school/school-setup/school-programs/SchoolProgramContent.vue";
import guideImage from "@/assets/image/school-setup-guide-woman.png";
import { $t } from "@/i18n";
import {
	openEditSchoolProgramModal,
	openCreateSchoolProgramModal,
	openConfirmDialog,
} from "@/components/modals/modalDefinitions";
import api from "@/api";
import { useToastStore } from "@/store/toast";
import { useDefaultStore } from "@/store";

definePage({
	name: "school.school-setup.programs",
});

const defaultStore = useDefaultStore();
const schoolStore = useSchoolStore();
const sessionStore = useSessionStore();
const toastStore = useToastStore();
const suggestions = ref<TTag[]>([]);
const isLoadingArchive = ref(false);
const isLoading = ref(false);
const dataTableColumns = computed(() => [
	{
		header: $t("app.school.schoolSetup.schoolPrograms.programName"),
		field: "tag_name",
	},
	{
		header: $t("app.school.schoolSetup.schoolPrograms.description"),
		field: "tag_description",
	},
	{
		header: $t("app.school.schoolSetup.schoolPrograms.dateCreated"),
		field: "tag_created",
	},
]);
const speechBubble = $t("app.school.schoolSetup.schoolPrograms.guideText", {
	supportEmail: defaultStore.supportEmail,
});

function getSuggestions(q: string) {
	q = q.trim().toLowerCase();
	if (schoolStore.schoolData && schoolStore.schoolData.programs) {
		suggestions.value = q
			? schoolStore.schoolData.programs.filter((program) =>
					program.tag_name.toLowerCase().includes(q),
				)
			: schoolStore.schoolData.programs;
	}
}

function editSchoolProgram(item: TTag) {
	openEditSchoolProgramModal(item);
}

function deleteProgram(program: TTag) {
	openConfirmDialog(
		$t("app.school.schoolSetup.schoolLocations.confirmArchivingDailog.title"),
		$t("app.school.schoolSetup.schoolLocations.confirmArchivingDailog.text", {
			item: program.tag_name,
		}),
		async () => {
			isLoadingArchive.value = true;
			try {
				const res = await api.deleteTag({
					tag_id: program.tag_id,
					tag_type: TAG_TYPES.PROGRAMS,
				});
				console.warn(res.data);

				schoolStore.schoolData = await schoolStore.fetchSchoolData(
					sessionStore.userData.scl_id,
				);
				toastStore.openToastSuccess(res.data.message);
			} catch (error) {
				console.error(error);
				toastStore.openToastError(error.response?.data?.message);
			} finally {
				isLoadingArchive.value = false;
			}
		},
	);
}

watch(
	() => schoolStore.schoolData?.programs,
	(newValue) => {
		if (newValue) {
			suggestions.value = newValue;
			isLoading.value = false;
		} else {
			isLoading.value = true;
		}
	},
	{ immediate: true },
);
</script>

<template>
	<div
		id="school-programs"
		:class="{ loading: isLoadingArchive }"
	>
		<school-management
			:page-icon="MaterialSymbolsBookmarkOutlineRounded"
			:page-title="$t('app.tags.pgrm', 2)"
			:create-btn-visible="true"
			:accordion-header="SchoolProgramsHeader"
			:items="suggestions"
			:accordion-content="SchoolProgramsContent"
			:colums="dataTableColumns"
			:speech-bubble-text="speechBubble"
			:guide-image="guideImage"
			:is-loading="isLoading"
			:action-btn-visible="true"
			@search-input="(e) => getSuggestions(e)"
			@edit-click="(e) => editSchoolProgram(e as TTag)"
			@archive-click="(e) => deleteProgram(e as TTag)"
			@create-new="openCreateSchoolProgramModal"
		/>
	</div>
</template>

<style lang="scss" scoped>
#school-programs {
	max-height: calc(100vh - $nav-top-bar - 1px);
	overflow-y: auto;
	overflow-x: auto;
	box-sizing: border-box;

	&.loading {
		cursor: wait;
		pointer-events: none;
	}
}

@media screen and (width >= 768px) {
	#school-programs {
		max-width: calc(100vw - $mini-sidebar-width);
	}
}

@media screen and (width >= 1157px) {
	#school-programs {
		max-width: calc(100vw - $expanded-sidebar-width);
	}
}
</style>
